/**
 * Token storage utilities for managing auth tokens in localStorage
 * Supports multiple providers simultaneously with active provider selection
 */

export type AIProvider =
  | "copilot"
  | "antigravity"
  | "openrouter"
  | "gemini"
  | "groq"
  | "perplexity"
  | "default";

export interface AuthTokens {
  provider: AIProvider;
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number; // Unix timestamp in milliseconds
  accountId?: string;
  deviceCode?: string; // For GitHub Copilot device flow
}

export interface MultiProviderTokens {
  copilot?: AuthTokens;
  antigravity?: AuthTokens;
  openrouter?: AuthTokens;
  gemini?: AuthTokens;
  groq?: AuthTokens;
  perplexity?: AuthTokens;
  default?: AuthTokens;
  activeProvider: AIProvider | null;
}

const STORAGE_KEY = "plotcat_auth_tokens";

/**
 * Save tokens for a specific provider
 */
export function saveTokens(tokens: AuthTokens): void {
  try {
    const stored = loadAllTokens();
    stored[tokens.provider] = tokens;
    // Set as active provider if no active provider exists
    if (!stored.activeProvider) {
      stored.activeProvider = tokens.provider;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    console.error("Failed to save tokens:", error);
  }
}

/**
 * Load all provider tokens
 */
export function loadAllTokens(): MultiProviderTokens {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { activeProvider: null };
    const parsed = JSON.parse(stored) as MultiProviderTokens & { openai?: AuthTokens };
    if (parsed.openai) {
      delete parsed.openai;
    }
    if ((parsed as { activeProvider?: string }).activeProvider === "openai") {
      parsed.activeProvider = null;
    }
    return parsed;
  } catch (error) {
    console.error("Failed to load tokens:", error);
    return { activeProvider: null };
  }
}

/**
 * Load tokens for active provider
 */
export function loadTokens(): AuthTokens | null {
  const all = loadAllTokens();
  if (!all.activeProvider) return null;
  return all[all.activeProvider] ?? null;
}

/**
 * Load tokens for a specific provider
 */
export function loadTokensForProvider(provider: AIProvider): AuthTokens | null {
  const all = loadAllTokens();
  return all[provider] ?? null;
}

/**
 * Get active provider
 */
export function getActiveProvider(): AIProvider | null {
  const all = loadAllTokens();
  return all.activeProvider;
}

/**
 * Set active provider
 */
export function setActiveProvider(provider: AIProvider): void {
  try {
    const stored = loadAllTokens();
    // Only allow setting active provider if that provider has tokens
    if (stored[provider]) {
      stored.activeProvider = provider;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch (error) {
    console.error("Failed to set active provider:", error);
  }
}

/**
 * Get list of connected providers
 */
export function getConnectedProviders(): AIProvider[] {
  const all = loadAllTokens();
  const providers: AIProvider[] = [];
  if (all.default) providers.push("default");
  if (all.copilot) providers.push("copilot");
  if (all.antigravity) providers.push("antigravity");
  if (all.openrouter) providers.push("openrouter");
  if (all.gemini) providers.push("gemini");
  if (all.groq) providers.push("groq");
  if (all.perplexity) providers.push("perplexity");
  return providers;
}

/**
 * Clear tokens for a specific provider
 */
export function clearTokensForProvider(provider: AIProvider): void {
  if (provider === "default") return; // Default provider cannot be disconnected
  try {
    const stored = loadAllTokens();
    delete stored[provider];
    // If we cleared the active provider, switch to another one or null
    if (stored.activeProvider === provider) {
      const remaining = getConnectedProviders();
      stored.activeProvider = remaining.length > 0 ? remaining[0] : null;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    console.error("Failed to clear tokens:", error);
  }
}

/**
 * Clear all tokens
 */
export function clearTokens(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear tokens:", error);
  }
}

/**
 * Check if tokens are expired (with 5-minute buffer)
 */
export function areTokensExpired(tokens: AuthTokens): boolean {
  if (!tokens.expiresAt) return false; // No expiry = doesn't expire
  const now = Date.now();
  const buffer = 5 * 60 * 1000; // 5 minutes
  return tokens.expiresAt - buffer < now;
}

/**
 * Check if tokens exist and are valid for active provider
 */
export function hasValidTokens(): boolean {
  const tokens = loadTokens();
  if (!tokens) return false;
  return !areTokensExpired(tokens);
}

/**
 * Get the current provider (if authenticated)
 */
export function getCurrentProvider(): AIProvider | null {
  return getActiveProvider();
}
